"use client";

import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./whatWeDo.css";

const imageUrls = {
  hero: "/assets/images/hero-banner.jpg",
  sap: "/assets/images/modernise-integrate-accelerate.jpg",
  data: "/assets/images/your-data-your-advantage.jpg",
  ai: "/assets/images/from-insight-to-impact.jpg",
  cta: "/assets/images/real-business-real-problem-real-solutions.jpg",
};

type IconName = "layers" | "database" | "sparkle" | "gear";

/* Saare icons ek hi 24px grid + ek hi stroke weight par — isliye consistent dikhte hain */
function Icon({ name, size = 24 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };

  if (name === "layers") {
    return (
      <svg {...common}>
        <path d="M12 2.5 2.5 7.5 12 12.5l9.5-5L12 2.5Z" />
        <path d="m2.5 12 9.5 5 9.5-5" />
        <path d="m2.5 16.5 9.5 5 9.5-5" />
      </svg>
    );
  }

  if (name === "database") {
    return (
      <svg {...common}>
        <ellipse cx="12" cy="5.5" rx="8" ry="3" />
        <path d="M4 5.5v13c0 1.7 3.6 3 8 3s8-1.3 8-3v-13" />
        <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
      </svg>
    );
  }

  if (name === "sparkle") {
    return (
      <svg {...common}>
        <path d="M12 3c.7 4.7 2.6 7.6 9 9-6.4 1.4-8.3 4.3-9 9-.7-4.7-2.6-7.6-9-9 6.4-1.4 8.3-4.3 9-9Z" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

const capabilityCards: {
  title: string;
  text: string;
  icon: IconName;
  href: string;
}[] = [
  {
    title: "Enterprise Transformation",
    text: "Modernise your SAP and enterprise ecosystem.",
    icon: "layers",
    href: "/enterprise-transformation",
  },
  {
    title: "Data & SAP BDC",
    text: "Turn data into business intelligence.",
    icon: "database",
    href: "/datasap",
  },
  {
    title: "AI & Intelligence",
    text: "Apply AI to solve real problems.",
    icon: "sparkle",
    href: "/whatwedo/Ai-and-Intelligence",
  },
  {
    title: "Operate & Improve",
    text: "Run better. Continuously improve. Create value.",
    icon: "gear",
    href: "/Operate",
  },
];

type LinkItem = string | { label: string; href: string };

/* Enterprise Transformation */
const sapLinksLeft: LinkItem[] = [
  { label: "SAP S/4HANA", href: "/enterprise-transformation/sap-s4hana" },
  { label: "Upgrades & Migrations", href: "/enterprise-transformation/upgrades-migrations" },
  { label: "RISE / GROW", href: "/enterprise-transformation/rise-grow" },
  { label: "SuccessFactors", href: "/enterprise-transformation/successfactors" },
];

const sapLinksRight: LinkItem[] = [
  { label: "BTP & Integration", href: "/enterprise-transformation/sap-btp-integration" },
  { label: "Cloud", href: "/enterprise-transformation/cloud" },
  { label: "Digital Engineering", href: "/enterprise-transformation/digital-engineering" },
];

/* Data & SAP BDC */
const dataLinksLeft: LinkItem[] = [
  { label: "Enterprise Data", href: "/datasap/enterprise-data" },
  { label: "Business Semantics", href: "/datasap/business-semantics" },
  { label: "Data Products", href: "/datasap/data-products" },
];

const dataLinksRight: LinkItem[] = [
  { label: "Analytics", href: "/datasap/analytics" },
  { label: "AI-ready Data", href: "/datasap/ai-ready-enterprise-data" },
];

/* AI & Intelligence */
const aiLinksLeft: LinkItem[] = [
  { label: "Enterprise AI", href: "/whatwedo/Ai-and-Intelligence/enterprise-ai" },
  { label: "Machine Learning", href: "/whatwedo/Ai-and-Intelligence/machine-learning" },
  { label: "Predictive Intelligence", href: "/whatwedo/Ai-and-Intelligence/predictive-intelligence" },
];

const aiLinksRight: LinkItem[] = [
  { label: "Decision Intelligence", href: "/whatwedo/Ai-and-Intelligence/decision-intelligence" },
  { label: "AI Agents", href: "/whatwedo/Ai-and-Intelligence/ai-agent" },
  { label: "ARIA", href: "#" },
];

/* Operate & Improve */
const operateLinksLeft: LinkItem[] = [
  { label: "Managed Services", href: "/Operate/managed-services" },
  { label: "ITSM", href: "/Operate/itsm" },
  { label: "Cybersecurity", href: "/Operate/cyber" },
];

const operateLinksRight: LinkItem[] = [
  { label: "Audit & Assurance", href: "/Operate/audit" },
  { label: "Controls & Compliance", href: "/Operate/controls-compliance" },
  { label: "Continuous Improvement", href: "/Operate/continuous-improvement" },
];

function LinkColumn({ items }: { items: LinkItem[] }) {
  return (
    <div className="wwdpg-link-column">
      {items.map((item) => {
        const label = typeof item === "string" ? item : item.label;
        const href = typeof item === "string" ? "#" : item.href;

        return (
          <a href={href} key={label}>
            <span>{label}</span>
            <span className="wwdpg-link-arrow">→</span>
          </a>
        );
      })}
    </div>
  );
}

export default function WhatWeDo() {
  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 100,
      mirror: false,
    });

    AOS.refresh();
  }, []);

  return (
    <main className="wwdpg-what-we-do-page">
      {/* ============================ HERO ============================ */}
      <section className="wwdpg-wwd-hero">
        <img className="wwdpg-hero-image" src={imageUrls.hero} alt="" />
        <div className="wwdpg-hero-overlay" />

        <div
          className="wwdpg-hero-content"
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="100"
        >
          <h1>
            End-to-End <span>Enterprise</span>
            <br />
            <span>Transformation</span>
          </h1>

          <p>
            We help organisations modernise, integrate and{" "}
            <br className="wwdpg-desktop-break" />
            intelligence-enable their core business — with SAP,{" "}
            <br className="wwdpg-desktop-break" />
            data, AI and managed services.
          </p>

          <a href="tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary wwdpg-primary-button">
            Talk to Our Experts <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>

      {/* ======================= CAPABILITY CARDS ===================== */}
      <section className="wwdpg-capability-grid" aria-label="Capabilities">
        {capabilityCards.map((card, i) => (
          <a
            href={card.href}
            className="wwdpg-capability-card"
            key={card.title}
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay={100 + i * 80}
          >
            <div className="wwdpg-capability-top">
              <span className="wwdpg-capability-icon">
                <Icon name={card.icon} size={24} />
              </span>
              <h3>{card.title}</h3>
            </div>

            <p>{card.text}</p>

            <div className="wwdpg-capability-foot">
              <span>Explore</span>
              <span className="wwdpg-card-arrow" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9h11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  <path d="m9.5 4.5 4.5 4.5-4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </a>
        ))}
      </section>

      {/* ===================== ENTERPRISE TRANSFORMATION ============== */}
      <section className="wwdpg-content-section wwdpg-enterprise-section" id="enterprise">
        <div
          className="wwdpg-enterprise-copy"
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="100"
        >
          <div className="wwdpg-eyebrow">ENTERPRISE TRANSFORMATION</div>
          <h2>Modernise. Integrate. Accelerate.</h2>

          <p>
            We help you unlock the full potential of SAP and
            next-gen technologies to build a resilient,
            scalable and future-ready enterprise.
          </p>

          <div className="wwdpg-link-columns">
            <LinkColumn items={sapLinksLeft} />
            <LinkColumn items={sapLinksRight} />
          </div>
        </div>

        <div
          className="wwdpg-section-image wwdpg-sap-image"
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="200"
        >
          <img src={imageUrls.sap} alt="Enterprise transformation" />

          <div className="wwdpg-sap-functions">
            <span>◉ &nbsp; Finance</span>
            <span>◉ &nbsp; HR</span>
            <span>◉ &nbsp; Supply Chain</span>
            <span>◉ &nbsp; Spend</span>
            <span>◉ &nbsp; Customer Experience</span>
          </div>
        </div>
      </section>

      {/* ========================= DATA & SAP BDC ===================== */}
      <section className="wwdpg-content-section wwdpg-data-section">
        <div
          className="wwdpg-data-copy"
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="100"
        >
          <div className="wwdpg-section-title-row">
            <span className="wwdpg-section-symbol">
              <Icon name="database" size={32} />
            </span>

            <div>
              <div className="wwdpg-eyebrow">DATA & SAP BDC</div>
              <h2>Your Data. Your Advantage.</h2>
            </div>
          </div>

          <p>
            We help you bring together enterprise data, business
            semantics and analytics — to create trusted, actionable
            intelligence for better decisions and AI-ready outcomes.
          </p>

          <div className="wwdpg-link-columns">
            <LinkColumn items={dataLinksLeft} />
            <LinkColumn items={dataLinksRight} />
          </div>
        </div>

        <div
          className="wwdpg-data-image"
          data-aos="fade-up"
          data-aos-duration="1000"
          data-aos-delay="150"
        >
          <img src={imageUrls.data} alt="Enterprise data and analytics" />
        </div>

        <div
          className="wwdpg-data-bdc-card"
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="300"
        >
          <div className="wwdpg-eyebrow">SAP BDC</div>
          <div className="wwdpg-bdc-pills">Secure &nbsp; | &nbsp; Compliant &nbsp; | &nbsp; Scalable</div>

          <p>
            Extract and model data from SAP for advanced analytics,
            AI and non-SAP use cases.
          </p>

          <a href="/datasap" className="wwdpg-bdc-link">
            Learn more <span>→</span>
          </a>
        </div>
      </section>

      {/* ======================= AI + OPERATE ========================= */}
      <section className="wwdpg-dual-section">
        <article className="wwdpg-dual-card wwdpg-ai-card">
          <div
            className="wwdpg-dual-image"
            data-aos="fade-up"
            data-aos-duration="900"
            data-aos-delay="100"
          >
            <img src={imageUrls.ai} alt="AI and intelligence" />
          </div>

          <div
            className="wwdpg-dual-copy"
            data-aos="fade-up"
            data-aos-duration="900"
            data-aos-delay="200"
          >
            <div className="wwdpg-section-title-row">
              <span className="wwdpg-section-symbol">
                <Icon name="sparkle" size={32} />
              </span>

              <div>
                <div className="wwdpg-eyebrow">AI & INTELLIGENCE</div>
                <h2>From Insight to Impact.</h2>
              </div>
            </div>

            <p>
              We build and deploy AI solutions and agents that
              augment your people, automate processes and unlock
              new possibilities.
            </p>

            <div className="wwdpg-link-columns">
              <LinkColumn items={aiLinksLeft} />
              <LinkColumn items={aiLinksRight} />
            </div>
          </div>
        </article>

        <article className="wwdpg-dual-card wwdpg-operate-card">
          <div
            className="wwdpg-dual-copy wwdpg-full-copy"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="150"
          >
            <div className="wwdpg-section-title-row">
              <span className="wwdpg-section-symbol">
                <Icon name="gear" size={32} />
              </span>

              <div>
                <div className="wwdpg-eyebrow">OPERATE & IMPROVE</div>
                <h2>Keep It Running. Keep It Growing.</h2>
              </div>
            </div>

            <p>
              We provide managed services and operational excellence
              to ensure stability, security and continuous improvement.
            </p>

            <div className="wwdpg-link-columns">
              <LinkColumn items={operateLinksLeft} />
              <LinkColumn items={operateLinksRight} />
            </div>
          </div>
        </article>
      </section>

      {/* ========================= FINAL CTA ========================== */}
      <section className="wwdpg-wwd-bottom-cta" id="contact">
        <img className="wwdpg-cta-bg" src={imageUrls.cta} alt="" aria-hidden="true" />

        <div
          className="wwdpg-cta-inner"
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="100"
        >
          <div className="wwdpg-cta-copy">
            <span className="wwdpg-cta-eyebrow">LET&apos;S SOLVE YOUR BUSINESS PROBLEM</span>
            <h2>Real business. Real problems. Real solutions.</h2>
            <p>Tell us what you&apos;re facing — we&apos;ll bring the right people, skills and solutions.</p>

            <div className="wwdpg-cta-actions">
              <a href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary wwdpg-primary-button">
                Tell Us Your Problem <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}